import { createMutationAtom } from 'atoms/create-resource-atom';
import {
  atomField,
  getFormHasChanges,
  getFormValuesAtom,
  required,
} from 'atoms/form-atoms';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { getMapDefaultConfig } from 'components/map/atoms';
import { atom, Getter } from 'jotai';
import { atomWithReducer, atomWithStorage } from 'jotai/utils';
import {
  atomWithLoadable,
  createResourceAtom,
  mutationAtomWithLoadable,
} from 'lib/atoms';
import request from 'lib/request';
import { useMemo } from 'react';
import { MapRef } from 'react-map-gl';
import {
  BundleOfSticksOwnershipResponse,
  CRMAccountSearchResult,
  CreateCrmAccountResponse,
  CRMPermissionsResponse,
  CRMTractsResponse,
  CrmTractAggregations,
  CrmTractFacets,
  EntityLinkResponse,
  EntityWithAccountsResponse,
  ReferenceLocationResponse,
  SlotNRIResponse,
  SubtractIntersectionAnalysisParameterResponse,
} from 'types/api-responses';
import {
  CRMTractForm,
  CRMTractRequest,
  CRMTractResponse,
  EntitySearchWithAccountsParams,
  InstrumentTypes,
  SubtractData,
  SubtractDetailsResponse,
  SubtractGeometryResponse,
  TiaStatus,
  TitleQuality,
  TitleRiskFactor,
  TractData,
  TractDetailsResponse,
  TractEnums,
  TractGeometryResponse,
  TractIntersectionAnalysis,
  TractJobResponse,
  WellboreExclusion,
  WellboreExclusionsBulkSetResponse,
  WellboreExclusionsResponse,
  WorkflowStatus,
  WorkflowStatusAction,
} from './types';
import { interestsCalculatorConfigAtom } from './interest-calculation-atoms';
import { STATUS_DISPLAY_MAP, allowedValuesFor, fixedNumber } from './utils';

export const errorAtom = atom<string | undefined>(undefined);

export const [tractEnumAtom, tractEnumLoadableAtom] = atomWithLoadable(
  async () => {
    const response = await request.get<TractEnums>('/crm/enums');
    return response as TractEnums;
  }
);

export interface EditTractState {
  tractId: string | null;
  subtractId: string | null;
}

export const editTractAtom = atom<EditTractState>({
  tractId: null,
  subtractId: null,
});

// Atom to fetch the latest workflow job for a tract being edited
// When subtractId is set, passes subtract_id to get data for that specific subtract
export const [restoredTractJobAtom, restoredTractJobLoadableAtom] =
  atomWithLoadable(async (get): Promise<TractJobResponse | null> => {
    const { tractId, subtractId } = get(editTractAtom);
    if (!tractId) {
      return null;
    }

    const queryParams = new URLSearchParams({ tract_id: tractId });
    if (subtractId) {
      queryParams.append('subtract_id', subtractId);
    }

    const response = await request.get<TractJobResponse>(
      `/api/jobs/processes/latest_for_tract?${queryParams.toString()}`
    );

    if ('error' in response) {
      return null;
    }

    return response;
  });

export const initializeRestoredWorkflowAtom = atom(
  (get) => get(editTractIdAtom),
  async (get, set) => {
    const job = await get(restoredTractJobAtom);
    if (!job) return;

    // Map steps to StatusItem format (matching useTractWorkflow format)
    const statusItems = job.steps
      .filter((step) => step.type)
      .map((step) => ({
        label: `${step.display_name}:`,
        value: STATUS_DISPLAY_MAP[step.status] || step.status,
        name: step.event_name,
        details: step.failure_reason || undefined,
      }));

    // Initialize workflowStatusAtom
    set(workflowStatusAtom, { type: 'initializeStatusItems', statusItems });
    set(workflowStatusAtom, {
      type: 'setOverallStatus',
      overallStatus: job.overall_status,
    });

    // Trigger fetches for detail atoms from step data_href
    job.steps.forEach((step) => {
      if (step.data_href) {
        switch (step.event_name) {
          case 'crm-tract-salesforce-create-status-changed':
          case 'crm-tract-salesforce-update-status-changed':
            set(createdTractDetailsAtom, step.data_href);
            break;
          case 'crm-subtract-salesforce-fetch-status-changed':
          case 'crm-subtract-salesforce-sync-status-changed':
            set(createdSubtractDetailsAtom, step.data_href);
            break;
          case 'crm-tract-geometry-create-status-changed':
          case 'crm-tract-geometry-update-status-changed':
            set(createdTractGeometryAtom, step.data_href);
            break;
          case 'crm-subtract-geometry-create-status-changed':
          case 'crm-subtract-geometry-update-status-changed':
            set(createdSubtractGeometryAtom, step.data_href);
            break;
          case 'crm-tract-bundle-of-sticks-status-changed':
            set(bundleOfSticksDetailsAtom, step.data_href);
            break;
        }
      }

      // Handle TIA step - set status to PROCESSED (no tia_id in restored workflow, uses collection endpoint)
      if (
        step.event_name === 'crm-tract-intersection-analysis-status-changed' &&
        step.status === 'processed'
      ) {
        set(tiaWorkflowStatusAtom, { status: 'PROCESSED' });
      }
    });
  }
);

initializeRestoredWorkflowAtom.onMount = (setAtom) => {
  setAtom();
};

export const editTractIdAtom = atom((get) => get(editTractAtom).tractId);

export const [crmTractFormAtom, crmTractFormLoadableAtom] =
  atomWithLoadable<CRMTractForm>(async (get) => {
    const tractEnum = await get(tractEnumAtom);
    await get(interestsCalculatorConfigAtom);
    const tractDetails = await get(createdTractDetailsAtom);
    const fixedLeaseRoyaltyRate = fixedNumber(
      allowedValuesFor(tractEnum.instrumentType, tractDetails?.assetType),
      'leaseRoyaltyRate'
    );

    // Prepare quarterCalls and lots from tractDetails
    const quarterCalls = tractDetails?.quarterCall
      ? tractDetails.quarterCall
          .split(',')
          .map((q: string) => q.trim())
          .filter(Boolean)
      : [];

    const lots = tractDetails?.lots
      ? tractDetails.lots
          .split(',')
          .map((lot: string) => lot.trim())
          .filter(Boolean)
      : [];

    // Determine recordType from tractDetails or use default
    // Note: recordType can be a string or an object with { id, value, label }
    const recordTypeValue = tractDetails?.recordType
      ? typeof tractDetails.recordType === 'string'
        ? tractDetails.recordType
        : (tractDetails.recordType as { value?: string; label?: string })
            .value ||
          (tractDetails.recordType as { value?: string; label?: string }).label
      : null;
    const matchingRecordType = recordTypeValue
      ? tractEnum.tractRecordType.find(
          (rt) => rt.value === recordTypeValue || rt.label === recordTypeValue
        )
      : null;
    const recordType =
      matchingRecordType?.value ?? tractEnum.tractRecordType[0].value;

    return {
      referenceLocation: atomField<ReferenceLocationResponse | undefined>(
        tractDetails?.referenceLocation as
          ReferenceLocationResponse | undefined,
        { validate: required }
      ),
      quarterCalls: atomField<string[]>(quarterCalls),
      lots: atomField<string[]>(lots),
      gridException: atomField<boolean>(tractDetails?.gridException ?? false),
      crmAccountId: atomField<string>(
        tractDetails?.account?.salesforceId ?? '',
        {
          validate: required,
        }
      ),
      recordType: atomField<TractEnums['tractRecordType'][number]['value']>(
        recordType,
        {
          validate: required,
        }
      ),
      tractFullLegalDescription: atomField<string>(
        tractDetails?.tractFullLegalDescription ?? ''
      ),
      instrumentType: atomField<InstrumentTypes['value']>(
        tractDetails?.assetType ?? '',
        {
          validate: required,
        }
      ),
      tractSource: atomField<TractEnums['tractSource'][number]['value']>(
        tractDetails?.tractSource ?? ''
      ),
      tractNetAcres: atomField<number | null>(
        tractDetails?.tractNetAcres ?? null,
        (() => {
          let hasBeenNonNull: boolean | null = null;
          return {
            validate: required,
            areEqual: (initial, current) => {
              if (current !== null) {
                hasBeenNonNull = true;
              }
              if (initial === null && current === null) {
                return !hasBeenNonNull;
              }
              return initial === current;
            },
          };
        })()
      ),
      netAcreageUnits: atomField<TractEnums['netAcreageUnit'][number]['value']>(
        tractDetails?.netAcreageUnits ?? '',
        {
          validate: required,
        }
      ),
      grossAcres: atomField<number | null>(tractDetails?.grossAcres ?? null, {
        validate: required,
      }),
      depthFullDescription: atomField<string>(
        tractDetails?.depthFullDescription ?? ''
      ),
      startDepth: atomField<number | null>(tractDetails?.startDepth ?? null),
      endDepth: atomField<number | null>(tractDetails?.endDepth ?? null),
      notes: atomField<string>(tractDetails?.notes ?? ''),
      alternateAssetID: atomField<string>(tractDetails?.altAssetId ?? ''),
      // Initialize BOS fields from subtract's bundleOfSticksOwnership in edit mode.
      // A rate the interest type fixes (UMI's 100%) starts at that rate.
      leaseRoyaltyRate: atomField<number | null>(
        fixedLeaseRoyaltyRate !== undefined
          ? fixedLeaseRoyaltyRate * 100
          : tractDetails?.subtracts?.[0]?.bundleOfSticksOwnership
                ?.leaseRoyaltyRate !== null &&
              tractDetails?.subtracts?.[0]?.bundleOfSticksOwnership
                ?.leaseRoyaltyRate !== undefined
            ? tractDetails.subtracts[0].bundleOfSticksOwnership
                .leaseRoyaltyRate * 100 // Convert decimal to percentage
            : null
      ),
      leaseRoyaltyType: atomField<string>(
        tractDetails?.subtracts?.[0]?.bundleOfSticksOwnership
          ?.leaseRoyaltyType ?? ''
      ),
      royaltyBurdens: atomField<number | null>(
        typeof tractDetails?.subtracts?.[0]?.bundleOfSticksOwnership
          ?.royaltyBurdens === 'number'
          ? tractDetails.subtracts[0].bundleOfSticksOwnership.royaltyBurdens *
              100 // Convert decimal to percentage
          : null
      ),
      orriBurdens: atomField<number | null>(
        typeof tractDetails?.subtracts?.[0]?.bundleOfSticksOwnership
          ?.orriBurdens === 'number'
          ? tractDetails.subtracts[0].bundleOfSticksOwnership.orriBurdens * 100 // Convert decimal to percentage
          : null
      ),
      titleQuality: atomField<TitleQuality>(
        tractDetails?.subtracts?.[0]?.titleQuality ?? 'none'
      ),
      titleRiskFactor: atomField<TitleRiskFactor>(
        tractDetails?.subtracts?.[0]?.titleRiskFactor ?? 'none'
      ),
    };
  });

export const isRecordTypeValidAtom = atom(async (get) => {
  const form = await get(crmTractFormAtom);
  return get(form.recordType).isValid;
});

export const isRiskAssessmentValidAtom = atom(async (get) => {
  const form = await get(crmTractFormAtom);
  const quality = get(form.titleQuality).value;
  const risk = get(form.titleRiskFactor).value;
  const qualitySet = quality !== 'none';
  const riskSet = risk !== 'none';
  return qualitySet === riskSet;
});

export const isTractValidAtom = atom(async (get) => {
  const form = await get(crmTractFormAtom);
  const riskValid = await get(isRiskAssessmentValidAtom);
  return (
    get(form.referenceLocation).isValid &&
    get(form.quarterCalls).isValid &&
    get(form.lots).isValid &&
    get(form.gridException).isValid &&
    get(form.tractFullLegalDescription).isValid &&
    get(form.instrumentType).isValid &&
    get(form.tractSource).isValid &&
    get(form.tractNetAcres).isValid &&
    get(form.netAcreageUnits).isValid &&
    get(form.grossAcres).isValid &&
    get(form.depthFullDescription).isValid &&
    get(form.startDepth).isValid &&
    get(form.endDepth).isValid &&
    get(form.notes).isValid &&
    get(form.alternateAssetID).isValid &&
    riskValid
  );
});

export const referenceLocationIdsAtom = atom(async (get) => {
  const form = await get(crmTractFormAtom);
  const referenceLocation = get(form.referenceLocation).value;
  return referenceLocation ? [referenceLocation.id] : [];
});

export const [isMapVisibleAtom, isMapVisibleLoadableAtom] = atomWithLoadable(
  async (get) => {
    const form = await get(crmTractFormAtom);
    const isGridException = get(form.gridException);
    const lots = get(form.lots);
    return (
      !isGridException.value &&
      lots.value.filter((lot) => lot.trim() !== '').length === 0
    );
  }
);

// Atom to track if an entity with linked account is selected
export const isEntityWithAccountSelectedAtom = atom(async (get) => {
  const form = await get(crmTractFormAtom);
  const crmAccountId = get(form.crmAccountId).value;
  return crmAccountId.length > 0;
});

export const validateNetAcresAtom = atom(null, async (get, set) => {
  const form = await get(crmTractFormAtom);
  const grossAcresValue = get(form.grossAcres).value;
  const netAcresValue = get(form.tractNetAcres).value;
  const netAcreageUnits = get(form.netAcreageUnits).value;

  // Only validate net acres <= gross acres when net acreage units is NMA
  // When units are NRA, net acres can exceed gross acres
  if (
    grossAcresValue !== null &&
    netAcresValue !== null &&
    netAcreageUnits === 'NMA'
  ) {
    if (netAcresValue > grossAcresValue) {
      set(form.tractNetAcres, {
        validate: () => {
          throw new Error(
            'Net Acres cannot be greater than Gross Acres when Net Acreage Units is NMA'
          );
        },
      });
    } else {
      set(form.tractNetAcres, { validate: required });
    }
  } else {
    set(form.tractNetAcres, { validate: required });
  }
});

export const createdTractAtom = atom<{
  tract: TractData | undefined;
  subtract: SubtractData | undefined;
}>({ tract: undefined, subtract: undefined });

/**
 * Convert a percentage value (e.g., 25.5) to decimal (e.g., 0.255).
 * Returns null if the value is null or undefined.
 * @param {number | null} value - The percentage value to convert
 * @return {number | null} The decimal value or null
 */
const percentageToDecimal = (value: number | null): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  return value / 100;
};
// Derived atom that returns the tract ID from either editTractIdAtom (for editing existing tracts)
// or createdTractDetailsAtom (for tracts created in the current session)
export const [activeTractIdAtom, activeTractIdLoadableAtom] = atomWithLoadable(
  async (get) => {
    const editTractId = get(editTractIdAtom);
    if (editTractId) return editTractId;

    const createdTractDetails = await get(createdTractDetailsAtom);
    return createdTractDetails?.id || null;
  }
);

export const [upsertCrmTractAtom, upsertCrmTractLoadableAtom] =
  mutationAtomWithLoadable(async (get) => {
    const form = await get(crmTractFormAtom);

    // Determine if this is an edit operation
    // Safe to use activeTractIdAtom here because mutationAtomWithLoadable
    // runs logic in the setter, where get() doesn't create reactive subscriptions
    const tractId = await get(activeTractIdAtom);
    const isEdit = !!tractId;

    // The server drops what the interest type does not carry and fixes what it
    // fixes (GET /crm/enums gives the same rules to the form), so the client
    // does not filter fields by type: a create sends every field, an edit sends
    // the changed fields as they stand.
    let requestPayload: Partial<CRMTractRequest> & { failureMode: string };

    if (isEdit) {
      // For edit: only include dirty (changed) fields
      const formValuesAtom = getFormValuesAtom(atom(form), 'dirty-values');
      const dirtyValues = get(formValuesAtom);

      // Build payload from dirty values, filtering out undefined
      requestPayload = {
        ...(dirtyValues.referenceLocation !== undefined && {
          referenceLocationId: dirtyValues.referenceLocation?.id,
        }),
        ...(dirtyValues.crmAccountId !== undefined && {
          crmAccountId: dirtyValues.crmAccountId,
        }),
        ...(dirtyValues.instrumentType !== undefined && {
          instrumentType: dirtyValues.instrumentType,
        }),
        ...(dirtyValues.tractNetAcres !== undefined && {
          tractNetAcres: dirtyValues.tractNetAcres,
        }),
        ...(dirtyValues.grossAcres !== undefined && {
          grossAcres: dirtyValues.grossAcres,
        }),
        ...(dirtyValues.quarterCalls !== undefined && {
          quarterCalls: dirtyValues.quarterCalls,
        }),
        ...(dirtyValues.lots !== undefined && {
          lots: dirtyValues.lots,
        }),
        ...(dirtyValues.tractFullLegalDescription !== undefined && {
          tractFullLegalDescription: dirtyValues.tractFullLegalDescription,
        }),
        ...(dirtyValues.depthFullDescription !== undefined && {
          depthFullDescription: dirtyValues.depthFullDescription,
        }),
        ...(dirtyValues.startDepth !== undefined && {
          startDepth: dirtyValues.startDepth,
        }),
        ...(dirtyValues.endDepth !== undefined && {
          endDepth: dirtyValues.endDepth,
        }),
        ...(dirtyValues.notes !== undefined && {
          notes: dirtyValues.notes,
        }),
        ...(dirtyValues.netAcreageUnits !== undefined && {
          netAcreageUnits: dirtyValues.netAcreageUnits,
        }),
        ...(dirtyValues.alternateAssetID !== undefined && {
          alternateAssetID: dirtyValues.alternateAssetID,
        }),
        ...(dirtyValues.gridException !== undefined && {
          gridException: dirtyValues.gridException,
        }),
        ...(dirtyValues.tractSource !== undefined && {
          tractSource: dirtyValues.tractSource,
        }),
        // Bundle of Sticks fields
        ...(dirtyValues.leaseRoyaltyRate !== undefined && {
          leaseRoyaltyRate: percentageToDecimal(dirtyValues.leaseRoyaltyRate),
        }),
        ...(dirtyValues.leaseRoyaltyType !== undefined && {
          leaseRoyaltyType: dirtyValues.leaseRoyaltyType,
        }),
        ...(dirtyValues.royaltyBurdens !== undefined && {
          royaltyBurdens: percentageToDecimal(dirtyValues.royaltyBurdens),
        }),
        ...(dirtyValues.orriBurdens !== undefined && {
          orriBurdens: percentageToDecimal(dirtyValues.orriBurdens),
        }),
        ...(dirtyValues.titleQuality !== undefined && {
          titleQuality: dirtyValues.titleQuality || 'none',
        }),
        ...(dirtyValues.titleRiskFactor !== undefined && {
          titleRiskFactor: dirtyValues.titleRiskFactor || 'none',
        }),
        failureMode: 'continue_on_failure',
      };
    } else {
      // For create: include all fields
      const referenceLocation = get(form.referenceLocation).value;
      const crmAccountId = get(form.crmAccountId).value;
      const instrumentType = get(form.instrumentType).value;
      const tractNetAcres = get(form.tractNetAcres).value;
      const grossAcres = get(form.grossAcres).value;
      const quarterCalls = get(form.quarterCalls).value;
      const lots = get(form.lots).value;
      const tractFullLegalDescription = get(
        form.tractFullLegalDescription
      ).value;
      const depthFullDescription = get(form.depthFullDescription).value;
      const startDepth = get(form.startDepth).value;
      const endDepth = get(form.endDepth).value;
      const notes = get(form.notes).value;
      const netAcreageUnits = get(form.netAcreageUnits).value;
      const alternateAssetID = get(form.alternateAssetID).value;
      const gridException = get(form.gridException).value;
      const tractSource = get(form.tractSource).value;
      // Bundle of Sticks fields (form values are percentages)
      const leaseRoyaltyRatePercent = get(form.leaseRoyaltyRate).value;
      const leaseRoyaltyType = get(form.leaseRoyaltyType).value;
      const royaltyBurdensPercent = get(form.royaltyBurdens).value;
      const orriBurdensPercent = get(form.orriBurdens).value;

      // Asset risk fields
      const titleQuality = get(form.titleQuality).value;
      const titleRiskFactor = get(form.titleRiskFactor).value;

      requestPayload = {
        referenceLocationId: referenceLocation?.id,
        crmAccountId,
        instrumentType,
        recordType: 'Oil and Gas',
        tractNetAcres,
        grossAcres,
        quarterCalls: quarterCalls,
        lots: lots,
        tractFullLegalDescription,
        depthFullDescription,
        startDepth,
        endDepth,
        notes,
        netAcreageUnits,
        alternateAssetID,
        gridException,
        tractSource,
        // Bundle of Sticks fields
        leaseRoyaltyRate: percentageToDecimal(leaseRoyaltyRatePercent),
        leaseRoyaltyType,
        royaltyBurdens: percentageToDecimal(royaltyBurdensPercent),
        orriBurdens: percentageToDecimal(orriBurdensPercent),
        titleQuality: titleQuality || 'none',
        titleRiskFactor: titleRiskFactor || 'none',
        failureMode: 'continue_on_failure',
      };
    }

    if (isEdit && !tractId) {
      throw new Error('Tract ID is required for editing.');
    }

    const response = isEdit
      ? await request.put<CRMTractResponse, { error: string }>(
          `/crm/tracts/${tractId}`,
          requestPayload,
          new AbortController()
        )
      : await request.post<CRMTractResponse, { error: string }>(
          '/crm/tracts',
          requestPayload,
          new AbortController()
        );

    if (response.type === 'success' && response.data) {
      return response.data;
    }

    // Handle error case
    const defaultMessage = isEdit
      ? 'Tract update failed.'
      : 'Tract creation failed.';
    const errorMessage =
      response.type === 'error' && response.data?.error
        ? `${defaultMessage} ${response.data.error}`
        : defaultMessage;
    throw new Error(errorMessage);
  });

// Derive notifications from base atom
export const [
  upsertCrmTractNotificationsAtom,
  upsertCrmTractNotificationsLoadableAtom,
] = atomWithLoadable(async (get) => {
  const response = await get(upsertCrmTractAtom);
  return response?.resources?.notifications;
});

// Wrapper for Wizard integration - triggers upsert and returns success/error
export const upsertCrmTractWizardActionAtom = atom(null, async (get, set) => {
  // Check if this is edit mode with no changes
  const tractId = await get(activeTractIdAtom);
  const isEdit = !!tractId;

  if (isEdit) {
    const form = await get(crmTractFormAtom);
    const formHasChangesAtom = getFormHasChanges(atom(form));
    const hasChanges = get(formHasChangesAtom);

    // Skip API call and workflow reset if no changes in edit mode
    if (!hasChanges) {
      return { success: true };
    }
  }

  // Reset workflow and TIA status before starting (only when there are changes to save)
  // TIA status must be reset so loadable atoms wait for new TIA ID from Pusher
  set(workflowStatusAtom, { type: 'reset' });
  set(tiaWorkflowStatusAtom, undefined);

  try {
    // set() returns the promise directly with mutationAtomWithLoadable
    await set(upsertCrmTractAtom);
    return { success: true };
  } catch (error) {
    return { success: false, error: (error as Error).message };
  }
});

const workflowStatusReducer = (
  state: WorkflowStatus,
  action: WorkflowStatusAction
): WorkflowStatus => {
  switch (action.type) {
    case 'reset':
      return { statusItems: [], overallStatus: 'pending' };
    case 'initializeStatusItems':
      return { ...state, statusItems: action.statusItems };
    case 'setOverallStatus':
      return { ...state, overallStatus: action.overallStatus };
    case 'updateStepStatus': {
      const formattedStatus =
        STATUS_DISPLAY_MAP[action.status] || action.status;

      return {
        ...state,
        statusItems: state.statusItems.map((item) => {
          if (item.name === action.name) {
            // Once things are in Completed, they should never move out
            // (handles out-of-order messages)
            if (item.value === 'Completed' && action.status !== 'processed') {
              return item;
            }
            return { ...item, value: formattedStatus, details: action.details };
          }
          return item;
        }),
      };
    }
  }
};

export const workflowStatusAtom = atomWithReducer<
  WorkflowStatus,
  WorkflowStatusAction
>({ statusItems: [], overallStatus: 'pending' }, workflowStatusReducer);

export const isWorkflowNotProcessingAtom = atom((get) => {
  const workflowStatus = get(workflowStatusAtom);
  return (
    workflowStatus.overallStatus === 'processed' ||
    workflowStatus.overallStatus === 'failed'
  );
});

/**
 * Whether the last `createdTractDetailsAtom` fetch failed at the transport
 * layer.
 *
 * `request.get` resolves an `{ error }` object instead of throwing, and the
 * fetcher below maps that to `null` — a contract four derived atoms depend on
 * (`crmTractFormAtom`, `activeTractIdAtom`, `nriReviewDataAtom`,
 * `selectedEntityAtom` all null-check it), so it must not start throwing.
 * That leaves the loadable in `hasData` with null data, which is also its
 * idle state — indistinguishable from "never fetched". This atom carries the
 * distinction for consumers that must not present a failure as an empty
 * answer, without changing what the fetcher returns.
 */
export const createdTractDetailsFailedAtom = atom(false);

export const [createdTractDetailsAtom, createdTractDetailsLoadableAtom] =
  mutationAtomWithLoadable<TractDetailsResponse | null, [string]>(
    async (_get, set, href: string): Promise<TractDetailsResponse | null> => {
      const response = await request.get<TractDetailsResponse>(href);
      if ('error' in response) {
        set(createdTractDetailsFailedAtom, true);
        return null;
      }
      set(createdTractDetailsFailedAtom, false);
      return response;
    }
  );

export const [createdSubtractDetailsAtom, createdSubtractDetailsLoadableAtom] =
  mutationAtomWithLoadable<SubtractDetailsResponse | null, [string]>(
    async (
      _get,
      _set,
      href: string
    ): Promise<SubtractDetailsResponse | null> => {
      const response = await request.get<SubtractDetailsResponse>(href);
      if ('error' in response) {
        return null;
      }
      return response;
    }
  );

export const [createdTractGeometryAtom, createdTractGeometryLoadableAtom] =
  mutationAtomWithLoadable<TractGeometryResponse | null, [string]>(
    async (_get, _set, href: string): Promise<TractGeometryResponse | null> => {
      const response = await request.get<TractGeometryResponse>(href);
      if ('error' in response) {
        return null;
      }
      return response;
    }
  );

export const [
  createdSubtractGeometryAtom,
  createdSubtractGeometryLoadableAtom,
] = mutationAtomWithLoadable<SubtractGeometryResponse | null, [string]>(
  async (
    _get,
    _set,
    href: string
  ): Promise<SubtractGeometryResponse | null> => {
    const response = await request.get<SubtractGeometryResponse>(href);
    if ('error' in response) {
      return null;
    }
    return response;
  }
);

// Bundle of Sticks ownership atom - fetches BOS data from href
export const [bundleOfSticksDetailsAtom, bundleOfSticksDetailsLoadableAtom] =
  mutationAtomWithLoadable<BundleOfSticksOwnershipResponse | null, [string]>(
    async (
      _get,
      _set,
      href: string
    ): Promise<BundleOfSticksOwnershipResponse | null> => {
      const response = await request.get<BundleOfSticksOwnershipResponse>(href);
      if ('error' in response) {
        return null;
      }
      return response;
    }
  );

export const tiaWorkflowStatusAtom = atom<
  { id?: number; channel?: string; status: TiaStatus } | undefined
>(undefined);

export const tractIntersectionAnalysisUatThresholdAtom = atom<
  Array<{ unitId: string; threshold: number }>
>([]);

export const minimumDevelopmentAreaCoverageAtom = atom<number | undefined>(
  undefined
);

// INS-8702: Counter atom bumped after a wellbore exclusion bulk_set mutation
// to force loadable atoms (current_development TIA, wellbore exclusions list)
// to re-fetch and reflect the new bucketing.
export const wellboreExclusionsRefreshAtom = atom(0);

// Counter atom bumped after an acreage basis save. The basis and its overlap
// override are read off the current_development payload, so without this the
// dropdown keeps rendering the pre-save value until a full page reload.
export const acreageBasisRefreshAtom = atom(0);

// Subscribes to wellboreExclusionsRefreshAtom so a successful bulk_set mutation
// re-fetches the current_development response, picking up the new bucketing
// (intersecting vs excludedByWellbore), and to acreageBasisRefreshAtom so a
// basis save picks up the stored parameters.
export const [
  currentDevelopmentTractIntersectionAnalysisAtom,
  currentDevelopmentTractIntersectionAnalysisLoadableAtom,
] = atomWithLoadable(async (get): Promise<TractIntersectionAnalysis | null> => {
  const tiaWorkflowStatus = get(tiaWorkflowStatusAtom);
  const subtractDetails = await get(createdSubtractDetailsAtom);
  get(wellboreExclusionsRefreshAtom);
  get(acreageBasisRefreshAtom);

  // Only fetch when TIA is processed and we have required data
  if (!tiaWorkflowStatus || tiaWorkflowStatus.status !== 'PROCESSED') {
    return null;
  }

  const subtractId = subtractDetails?.id;
  if (!subtractId) return null;

  // Use member URL if id exists (from Pusher), collection URL if not (from restored workflow)
  const url = tiaWorkflowStatus.id
    ? `/crm/subtracts/${subtractId}/tract_intersection_analyses/${tiaWorkflowStatus.id}/current_development`
    : `/crm/subtracts/${subtractId}/tract_intersection_analyses/current_development`;

  const response = await request.get<TractIntersectionAnalysis>(
    url,
    new AbortController()
  );

  if ('error' in response) {
    return null;
  }

  return response;
});

// Subscribes to wellboreExclusionsRefreshAtom because the future_development
// jbuilder shares the slots_summary partial with current_development; both
// summaries reflect current-development wellbore exclusion counts.
export const [
  futureDevelopmentTractIntersectionAnalysisAtom,
  futureDevelopmentTractIntersectionAnalysisLoadableAtom,
] = atomWithLoadable(async (get): Promise<TractIntersectionAnalysis | null> => {
  const tiaWorkflowStatus = get(tiaWorkflowStatusAtom);
  const subtractDetails = await get(createdSubtractDetailsAtom);
  get(wellboreExclusionsRefreshAtom);

  // Only fetch when TIA is processed and we have required data
  if (!tiaWorkflowStatus || tiaWorkflowStatus.status !== 'PROCESSED') {
    return null;
  }

  const subtractId = subtractDetails?.id;
  if (!subtractId) return null;

  // Use member URL if id exists (from Pusher), collection URL if not (from restored workflow)
  const url = tiaWorkflowStatus.id
    ? `/crm/subtracts/${subtractId}/tract_intersection_analyses/${tiaWorkflowStatus.id}/future_development`
    : `/crm/subtracts/${subtractId}/tract_intersection_analyses/future_development`;

  const response = await request.get<TractIntersectionAnalysis>(
    url,
    new AbortController()
  );

  if ('error' in response) {
    return null;
  }

  return response;
});

export const retriggerTractIntersectionAnalysisAtom = atom(
  null,
  async (
    get,
    set,
    data:
      | {
          uatCoverageThreshold: number;
          unitId: string;
        }
      | {
          unitCoverageThreshold?: number;
          minimumDevelopmentAreaCoverage?: number;
        }
  ) => {
    const tiaWorkflowStatus = get(tiaWorkflowStatusAtom);
    if (!tiaWorkflowStatus) {
      return;
    }
    set(tiaWorkflowStatusAtom, { ...tiaWorkflowStatus, status: 'IDLE' });
    const subtractDetails = await get(createdSubtractDetailsAtom);
    const subtractId = subtractDetails?.id;
    const urlSegment = 'unitId' in data ? `${data.unitId}` : 'global';
    const url = `/crm/subtracts/${subtractId}/subtract_intersection_coverage_parameters/${urlSegment}`;
    const payload = !('unitId' in data)
      ? {
          subtract_intersection_coverage_parameter: {
            ...(data.unitCoverageThreshold !== undefined && {
              coverage_threshold: data.unitCoverageThreshold,
            }),
            ...(data.minimumDevelopmentAreaCoverage !== undefined && {
              minimum_development_area_coverage:
                data.minimumDevelopmentAreaCoverage,
            }),
          },
        }
      : {
          subtract_intersection_coverage_parameter: {
            uat_coverage_threshold: data.uatCoverageThreshold,
          },
        };

    const response = await request.patch<
      {
        analysis: {
          id: number;
          channel: string;
        };
      },
      unknown
    >(url, payload, new AbortController());
    if (response.type === 'success') {
      set(tiaWorkflowStatusAtom, {
        id: response.data.analysis.id,
        channel: response.data.analysis.channel,
        status: 'READY',
      });
      return { success: true };
    } else {
      set(tiaWorkflowStatusAtom, undefined);
      return {
        success: false,
        error: 'Failed to update tract intersection analysis parameters',
      };
    }
  }
);

export const isTractIntersectionAnalysisCurrentNextEnabled = atom((get) => {
  const tiaWorkflowStatus = get(tiaWorkflowStatusAtom);
  const currentDevLoadable = get(
    currentDevelopmentTractIntersectionAnalysisLoadableAtom
  );
  const futureDevLoadable = get(
    futureDevelopmentTractIntersectionAnalysisLoadableAtom
  );
  const isWorkflowNotProcessing = get(isWorkflowNotProcessingAtom);

  // TIA is in progress if workflow status is READY (running) or loadables are loading
  const isTiaInProgress =
    tiaWorkflowStatus?.status === 'READY' ||
    currentDevLoadable.state === 'loading' ||
    futureDevLoadable.state === 'loading';

  // Enable when tract creation workflow is processed and TIA is not in progress
  return isWorkflowNotProcessing && !isTiaInProgress;
});

// Disable back button from Current Development when user doesn't have CRM create permission
export const isPreviousEnabledFromCurrentDevelopmentAtom = atom(async (get) => {
  const crmPermission = await get(crmCreatePermissionAtom);
  const hasCrmCreatePermission = crmPermission?.hasPermissions ?? false;

  // If no permission, disable back button
  if (!hasCrmCreatePermission) {
    return false;
  }

  // Otherwise, use the existing TIA check
  return get(isTractIntersectionAnalysisCurrentNextEnabled);
});

// Private: read and written only through refetchNriDataAtom below.
const nriReviewDataRefetchAtom = atom(0);

// Readable as well as writable so the counter behind it stays private: callers
// set it to trigger a refetch, and a test can read how many have happened
// without the counter itself becoming production surface.
export const refetchNriDataAtom = atom(
  (get) => get(nriReviewDataRefetchAtom),
  (get, set) => set(nriReviewDataRefetchAtom, (c) => c + 1)
);

// Async atom that depends on refetch trigger
export const [nriReviewDataAtom, nriReviewDataLoadableAtom] = atomWithLoadable(
  async (get: Getter) => {
    get(nriReviewDataRefetchAtom);

    const [tractDetails, subtractDetails] = await Promise.all([
      get(createdTractDetailsAtom),
      get(createdSubtractDetailsAtom),
    ]);
    const subtractId = subtractDetails?.id;

    if (!subtractId) return null;

    const response = await request.get<SlotNRIResponse>(
      `/crm/subtracts/${subtractId}/slot_allocations`
    );

    if ('error' in response) return null;

    // Extract fallback account from tract details
    const fallbackAccount = tractDetails?.account
      ? {
          salesforceId: tractDetails.account.salesforceId,
          salesforceHref: tractDetails.account.salesforceHref ?? null,
          uuid: tractDetails.account.uuid ?? null,
          name: tractDetails.account.name,
        }
      : null;

    // Inject fallback account into tracts where crmAccount is null
    return {
      ...response,
      slots: response.slots.map((slot) => ({
        ...slot,
        tracts: (slot.tracts ?? []).map((tract) => ({
          ...tract,
          crmAccount: tract.crmAccount ?? fallbackAccount,
        })),
      })),
    };
  }
);

/**
 * Helper atoms
 */

export const mapConfigAtom = atomWithStorage(
  'insights-crm-tracts-map-config',
  getMapDefaultConfig()
);

/**
 * Map config for the scoped map panel. General tile layers default to
 * OFF so they don't overlap with the scoped GeoJSON layers. Users can
 * still turn them on via the "General" section in the sidebar.
 */
export const scopedMapConfigAtom = atomWithStorage(
  'insights-crm-tracts-scoped-map-config',
  getMapDefaultConfig({
    wells: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        vertical: false,
      },
    },
    wellsV2: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        vertical: false,
        horizontal: false,
        horizontalPermit: false,
        horizontalDuc: false,
        horizontalProducing: false,
        horizontalShutIn: false,
        horizontalExpiredPermit: false,
        horizontalInactive: false,
        horizontalPrePermit: false,
        horizontalApd: false,
        horizontalProposal: false,
        horizontalPlanned: false,
        verticalPermit: false,
        verticalDuc: false,
        verticalProducing: false,
        verticalShutIn: false,
        verticalExpiredPermit: false,
        verticalInactive: false,
        verticalPrePermit: false,
        verticalApd: false,
        verticalProposal: false,
        verticalPlanned: false,
        permit: false,
        duc: false,
        producing: false,
        shutIn: false,
        expiredPermit: false,
        inactive: false,
        prePermit: false,
        apd: false,
        proposal: false,
        planned: false,
      },
    },
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
    titleEntityAssets: {
      checked: false,
      options: {
        surfaceInterest: false,
        workingInterest: false,
        unleased: false,
        royaltyInterest: false,
      },
    },
    basinTier: {
      checked: false,
      options: {
        basin: false,
        tier: false,
      },
    },
  })
);

export const [crmCreatePermissionAtom, crmCreatePermissionLoadableAtom] =
  atomWithLoadable(async () => {
    const response =
      await request.get<CRMPermissionsResponse>('/crm/permissions');
    return response as CRMPermissionsResponse;
  });

export const mapRefAtom = atom<MapRef | null>(null);

// Atom to control wizard step navigation
// When set to a step index, the wizard navigates to that step and resets the atom to null
export const wizardNavigateToStepAtom = atom<number | null>(null);

/**
 * A fresh entity search, for a page that mounts its own picker.
 * @return {object} A searchAtomPair('entities')
 */
export const createEntitySearchAtom = () =>
  searchAtomPair<EntityWithAccountsResponse, EntitySearchWithAccountsParams>(
    'entities'
  );

export const entitySearchAtom = createEntitySearchAtom();

export const [selectedEntityAtom, selectedEntityLoadableAtom] =
  atomWithLoadable(async (get) => {
    const tractDetails = await get(createdTractDetailsAtom);

    if (!tractDetails) {
      return {
        entities: [],
        entity: undefined,
        account: undefined,
      };
    }

    const entitiesFromTract = tractDetails.entities || [];
    const entities = entitiesFromTract.map((entityFromTract) => ({
      id: entityFromTract.id,
      name: entityFromTract.name,
      href: `/entities/${entityFromTract.id}`,
      address: entityFromTract.address,
      type: entityFromTract.type,
    }));

    const entity = entities.length > 0 ? entities[0] : undefined;
    const account = tractDetails.account
      ? {
          salesforceId: tractDetails.account.salesforceId,
          name: tractDetails.account.name,
          type: tractDetails.account.type,
          billingAddress: tractDetails.account.address,
          salesforceHref: tractDetails.account.salesforceHref,
        }
      : undefined;

    return {
      entities,
      entity,
      account,
    };
  });

// Atom to prepare all data for tract summary display
export const [tractSummaryDataAtom, tractSummaryDataLoadableAtom] =
  atomWithLoadable(async (get) => {
    const selectedEntityData = await get(selectedEntityAtom);

    const { entity, account, entities } = selectedEntityData;
    const hasAccountButNoEntity = !!account && !entity;
    const hasMultipleEntities = entities && entities.length > 1;

    // Determine what to show in the Insights Entity title section
    let insightsEntityDisplay:
      | { type: 'linkButton'; label: string }
      | { type: 'multipleEntities'; count: number }
      | { type: 'singleEntity'; entity?: { name: string; href: string } };

    if (hasAccountButNoEntity) {
      insightsEntityDisplay = {
        type: 'linkButton',
        label: 'Link Insights Entity',
      };
    } else if (hasMultipleEntities) {
      insightsEntityDisplay = {
        type: 'multipleEntities',
        count: entities.length,
      };
    } else {
      insightsEntityDisplay = {
        type: 'singleEntity',
        entity: entity
          ? {
              name: entity.name,
              href: entity.href ?? '',
            }
          : undefined,
      };
    }

    // Determine if address and type should be shown
    const showEntityDetails = !hasAccountButNoEntity && !hasMultipleEntities;

    return {
      entity: showEntityDetails ? entity : undefined,
      account,
      entities,
      insightsEntityDisplay,
      showEntityDetails,
      hasMultipleEntities,
    };
  });

export const entityLinkingMutationAtom = createMutationAtom<
  EntityLinkResponse,
  'entityLinks'
>();

export const createCrmAccountMutationAtom = createMutationAtom<
  CreateCrmAccountResponse,
  'createCrmAccount'
>();

// ─────────────────────────────────────
// CRM Tract Library Search
// ─────────────────────────────────────

export interface CrmTractSearchParams extends SearchParams {
  /** Only these tracts (Salesforce ids). */
  tractIds?: string[];
  /** Only the tracts this document vests or lies in the survey areas of. */
  documentId?: number;
  entityIds?: string[];
  accountIds?: string[];
  instrumentType?: string[];
  /**
   * One or more of VestingDocument::STATUSES — 'none' | 'needs_review' | 'verified'.
   *
   * Snake_case VALUES, camelCase KEY. The key is underscored for us by rails-boost, which
   * transforms the request body before every action; the values are data and are never touched,
   * so they must match the strings the index actually holds.
   */
  vestingStatus?: string[];
  /** True narrows to tracts holding a linked workbook; false is never sent. */
  hasExternalTitleSupport?: boolean;
  /** True narrows to live tracts of portfolio-investment entities; false is never sent. */
  portfolioAssets?: boolean;
  createdAt?: { start?: string; end?: string };
  updatedAt?: { start?: string; end?: string };
}

export const createCrmTractSearchAtom = () => {
  return searchAtomPair<
    CRMTractsResponse,
    CrmTractSearchParams,
    CrmTractFacets,
    CrmTractAggregations
  >({ href: '/queries/crm/tracts_and_subtracts' });
};

export const participationFactorMutationAtom = createMutationAtom<
  SubtractIntersectionAnalysisParameterResponse,
  'subtractIntersectionAnalysisParameter'
>();

export const crmAccountsSearchAtom = createMutationAtom<
  CRMAccountSearchResult,
  'crmAccountsSearch'
>();

export const useParticipationFactorAtom = (subtractId: string | undefined) => {
  const href = subtractId
    ? `/crm/subtracts/${subtractId}/subtract_intersection_analysis_parameters`
    : undefined;
  return useMemo(
    () =>
      createResourceAtom<SubtractIntersectionAnalysisParameterResponse>(href),
    [href]
  );
};

// ─── Wellbore exclusions (INS-8702) ─────────────────────────────────────────
//
// State model:
//   wellboreExclusionsRefreshAtom — counter bumped after a bulk_set mutation;
//                                    declared above with currentDevelopmentTractIntersectionAnalysisAtom
//                                    so both loadables can subscribe.
//   wellboreExclusionsAtom        — loadable list of WellboreExclusion records
//                                    fetched from GET /crm/subtracts/:id/wellbore_exclusions.
//   stagedExcludedUwisAtom        — Set<UWI> capturing in-flight checkbox state
//                                    while the Manage Slots modal is open.
//                                    Cleared/reset on modal open and on save.
//   bulkSetWellboreExclusionsAtom — action atom: PUT the staged set, refresh
//                                    on success.

export const [wellboreExclusionsAtom, wellboreExclusionsLoadableAtom] =
  atomWithLoadable(async (get): Promise<WellboreExclusion[]> => {
    const subtractDetails = await get(createdSubtractDetailsAtom);
    get(wellboreExclusionsRefreshAtom);

    const subtractId = subtractDetails?.id;
    if (!subtractId) return [];

    const response = await request.get<WellboreExclusionsResponse>(
      `/crm/subtracts/${subtractId}/wellbore_exclusions`,
      new AbortController()
    );
    if ('error' in response) return [];
    return response.wellboreExclusions;
  });

export const stagedExcludedUwisAtom = atom<Set<string>>(new Set<string>());

export const bulkSetWellboreExclusionsAtom = atom(
  null,
  async (
    get,
    set,
    args: { excludedUwis: string[]; reason?: string }
  ): Promise<
    | { success: true; result: WellboreExclusionsBulkSetResponse }
    | { success: false; error: string }
  > => {
    const subtractDetails = await get(createdSubtractDetailsAtom);
    const subtractId = subtractDetails?.id;
    if (!subtractId) {
      return { success: false, error: 'No subtract selected' };
    }

    const response = await request.put<
      WellboreExclusionsBulkSetResponse,
      { error: string }
    >(
      `/crm/subtracts/${subtractId}/wellbore_exclusions`,
      { excluded_uwis: args.excludedUwis, reason: args.reason },
      new AbortController()
    );

    if (response.type !== 'success') {
      const failureData =
        'data' in response && response.data ? response.data : null;
      return {
        success: false,
        error:
          failureData && 'error' in failureData
            ? failureData.error
            : 'Failed to update wellbore exclusions',
      };
    }

    // Bump the refresh counter so the loadable refetches with the new state.
    set(wellboreExclusionsRefreshAtom, (n) => n + 1);
    return { success: true, result: response.data };
  }
);
